import YLLWLOGO from "../../assets/Yllw_Inventory.svg";
import WelcomeBackground from "../../assets/welcome-loading-screen.png";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import { Link } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setAccessToken } from "../../services/authService";
import { SoecoButton } from "../../components/SoecoButton";
import { post } from "../../services/restClient";
import { EmployeeContext } from "../../context/EmployeeContext";
import { LoginForm, Role } from "../../types";
import { Endpoint, setEndpoint } from "../../services/endpoints";
import { useSnackBar } from "../../context/SnackbarContext";
import { FormControl, IconButton, InputAdornment, Typography } from "@mui/material";
import { Eye, EyeOff } from "lucide-react";

export const LoginPage = () => {
    const [loginForm, setLoginForm] = useState<LoginForm>({
        email: "",
        password: "",
    });
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

    const [showPassword, setShowPassword] = useState<{ current: boolean; new: boolean }>({
        current: false,
        new: false,
    });
    const employee = useContext(EmployeeContext);
    const navigate = useNavigate();
    const snackBar = useSnackBar();

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    const handleLogin = (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        type RequestBody = {
            email: string;
            password: string;
        };

        type ResponseBody = RequestBody & {
            accessToken: string;
        };
        post<RequestBody, ResponseBody>(setEndpoint(Endpoint.login), {
            email: loginForm.email.toLowerCase(),
            password: loginForm.password,
        })
            .then(res => {
                const token = setAccessToken(res.accessToken);
                employee.setEmployee({
                    companyId: token.companyId,
                    id: token.employeeId,
                    role: token.role as Role,
                });
                navigate("/");
            })
            .catch(error => {
                if (error instanceof Error) {
                    snackBar.showSnackBar(error.message, "error");
                }
            });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLoginForm({
            ...loginForm,
            [e.target.name]: e.target.value,
        });
    };
    const handleClickShowPassword = (target: string) => {
        target === "current"
            ? setShowPassword({ ...showPassword, current: !showPassword.current })
            : setShowPassword({ ...showPassword, new: !showPassword.new });
    };
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return !isOnline ? (
        <OfflineWrapper>
            {/* <OfflineBackground src={WelcomeBackground} alt="Offline background" /> */}
        </OfflineWrapper>
    ) : (
        <LoginWrapper>
            <Form noValidate autoComplete="off">
                <div>
                    <Logo src={YLLWLOGO} />
                </div>
                <div>
                    <Typography
                        fontWeight={600}
                        fontSize={24}
                        sx={{ fontFamily: "Inter, sans-serif" }}
                    >
                        Sign in
                    </Typography>
                    <Typography
                        fontSize={16}
                        color="#8c8c8c"
                        fontWeight={600}
                        sx={{ fontFamily: "Inter, sans-serif" }}
                    >
                        Yllw Inventory App
                    </Typography>
                </div>
                <LoginFormWrapper>
                    <TextFields>
                        <TextField
                            label="Email"
                            name="email"
                            variant="outlined"
                            onChange={handleChange}
                            inputProps={{
                                style: {
                                    textTransform: "lowercase", // force lowercase on email
                                },
                            }}
                        />
                        <TextField
                            label="Password"
                            name="password"
                            type={showPassword.current ? "text" : "password"}
                            variant="outlined"
                            onChange={handleChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            name="current"
                                            aria-label="toggle password visibility"
                                            onClick={() => handleClickShowPassword("current")}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {showPassword.current ? <EyeOff /> : <Eye />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </TextFields>
                    <SoecoButton color="primary" type="submit" onClick={e => handleLogin(e)}>
                        Login
                    </SoecoButton>
                </LoginFormWrapper>
            </Form>
            <ForgotPasswordLink to="/forgotPassword">Forgot your password?</ForgotPasswordLink>
        </LoginWrapper>
    );
};
const OfflineWrapper = styled.div`
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    position: relative;
`;

const OfflineBackground = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const LoginWrapper = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
    gap: 20px;
`;

const Form = styled.form`
    > * {
        width: 100%;
        :not(:last-child) {
            margin-bottom: 20px;
        }
    }
`;

const ForgotPasswordLink = styled(Link)`
    text-decoration: none;
    color: #8c8c8c;
    font-size: 16px;
    font-weight: 500;
    font-family: "Inter", sans-serif;
`;

const Logo = styled.img`
    margin-bottom: 25%;
    margin-top: 55%;
    height: 50px;
    width: 300px;
`;

const LoginFormWrapper = styled(FormControl)`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const TextFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;
